import { useEffect, useState } from 'react';
import { Checkbox } from '@nimbus-ds/components';
import { FormField } from '@nimbus-ds/patterns';
import { OptionsBusinessInfoResponseDto } from '@tiendanube/common';
import { BaseCard, Grid, Input, Select, Title } from '@tiendanube/components';
import useScrollToError from 'App/components/AppLayout/useScrollToError';
import { InputNumberNimbus } from 'commons/components';
import Stack from 'commons/components/Stack';
import { InterfaceNameValue } from 'commons/types';
import {
  useTranslationAccount,
  useShowTaxExceptionsCard,
} from 'domains/Account/hooks';
import TaxExceptionsCard from '../TaxExceptionsCard';

export type onChangeType = (data: {
  name: string;
  value: string | number | boolean;
}) => void;

export type GeneralInformationBusinessDataCardValues = {
  ownerPhoneType: string;
  ownerPhoneCountry: string;
  ownerPhoneArea: string;
  phoneNumber: string;
  timeZone: string;
  brickAndMortars: string;
  businessSize: string;
  type: string;
  orderTaxesEnabled: boolean;
  orderTaxesPercent: number;
};
export interface GeneralInformationBusinessDataCardProps {
  values: GeneralInformationBusinessDataCardValues;
  options: OptionsBusinessInfoResponseDto;
  onChange: onChangeType;
  shouldDisplayTaxesInfo?: boolean;
  errors: Partial<Record<string, string>>;
}

function GeneralInformationBusinessDataCard({
  values,
  options,
  onChange,
  shouldDisplayTaxesInfo,
  errors,
}: GeneralInformationBusinessDataCardProps): JSX.Element {
  const t = useTranslationAccount();
  const [taxValue, setTaxValue] = useState('0');
  const showTaxExceptionsCard = useShowTaxExceptionsCard();
  const {
    ownerPhoneTypeOptions,
    timeZoneOptions,
    brickAndMortarsOptions,
    businessSizeOptions,
    typeOptions,
  } = options;

  useEffect(() => {
    setTaxValue(values.orderTaxesPercent.toString());
  }, [values.orderTaxesPercent]);

  const handleChangeTaxValue = ({ name, value }: InterfaceNameValue) => {
    setTaxValue(value);
    onChange({
      name,
      value,
    });
  };

  useScrollToError(errors);

  const orderTaxesPercentError = errors['orderTaxesPercent'];

  const renderTaxesInfo = () => {
    if (!shouldDisplayTaxesInfo) return null;
    return (
      <>
        <Checkbox
          label={t(
            'businessDataPage.generalInformationCard.orderTaxesEnabled.label',
          )}
          name="orderTaxesEnabled"
          checked={values.orderTaxesEnabled}
          onChange={(event) => {
            onChange({
              name: 'orderTaxesEnabled',
              value: event.target.checked,
            });
          }}
        />
        {values.orderTaxesEnabled && (
          <FormField
            label={t(
              'businessDataPage.generalInformationCard.orderTaxesPercent.label',
            )}
            helpText={orderTaxesPercentError ? t(orderTaxesPercentError) : ''}
            showHelpText={!!orderTaxesPercentError}
            appearance={orderTaxesPercentError ? 'danger' : 'none'}
            id="orderTaxesPercent"
          >
            <InputNumberNimbus
              type="float"
              name="orderTaxesPercent"
              placeholder={t(
                'businessDataPage.generalInformationCard.orderTaxesPercent.placeholder',
              )}
              onChange={({ target: { name, value } }) => {
                handleChangeTaxValue({ name, value });
              }}
              value={taxValue}
              append="%"
              appendPosition="end"
              appearance={orderTaxesPercentError ? 'danger' : 'neutral'}
            />
          </FormField>
        )}
      </>
    );
  };
  return (
    <>
      <BaseCard>
        <BaseCard.Header>
          <Title type="h3">
            {t('businessDataPage.generalInformationCard.title') as string}
          </Title>
        </BaseCard.Header>
        <BaseCard.Body>
          <Stack column align="stretch" spacing="loose">
            <Select
              name="ownerPhoneType"
              label={t(
                'businessDataPage.generalInformationCard.ownerPhoneType.label',
              )}
              placeholder={t(
                'businessDataPage.generalInformationCard.ownerPhoneType.placeholder',
              )}
              options={ownerPhoneTypeOptions}
              value={values.ownerPhoneType}
              onChange={onChange}
            />

            <Stack.Item>
              <Grid>
                <Grid.Row>
                  <Grid.Column sm={2} md={2} lg={2}>
                    <Input
                      type="number"
                      label={t(
                        'businessDataPage.generalInformationCard.ownerPhoneCountry.label',
                      )}
                      placeholder={t(
                        'businessDataPage.generalInformationCard.ownerPhoneCountry.placeholder',
                      )}
                      onChange={onChange}
                      name="ownerPhoneCountry"
                      value={values.ownerPhoneCountry}
                    />
                  </Grid.Column>

                  <Grid.Column sm={2} md={2} lg={2}>
                    <Input
                      type="number"
                      label={t(
                        'businessDataPage.generalInformationCard.ownerPhoneArea.label',
                      )}
                      placeholder={t(
                        'businessDataPage.generalInformationCard.ownerPhoneArea.placeholder',
                      )}
                      onChange={onChange}
                      name="ownerPhoneArea"
                      value={values.ownerPhoneArea}
                    />
                  </Grid.Column>

                  <Grid.Column sm={2} md={2} lg={2}>
                    <Input
                      type="number"
                      label={t(
                        'businessDataPage.generalInformationCard.phoneNumber.label',
                      )}
                      placeholder={t(
                        'businessDataPage.generalInformationCard.phoneNumber.placeholder',
                      )}
                      onChange={onChange}
                      name="phoneNumber"
                      value={values.phoneNumber}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Stack.Item>

            <Select
              name="timeZone"
              label={t(
                'businessDataPage.generalInformationCard.timeZone.label',
              )}
              placeholder={t(
                'businessDataPage.generalInformationCard.timeZone.placeholder',
              )}
              options={timeZoneOptions}
              value={values.timeZone}
              onChange={onChange}
            />

            <Select
              name="brickAndMortars"
              label={t(
                'businessDataPage.generalInformationCard.brickAndMortars.label',
              )}
              placeholder={t(
                'businessDataPage.generalInformationCard.brickAndMortars.placeholder',
              )}
              options={brickAndMortarsOptions}
              value={values.brickAndMortars}
              onChange={onChange}
            />

            <Select
              name="businessSize"
              label={t(
                'businessDataPage.generalInformationCard.businessSize.label',
              )}
              placeholder={t(
                'businessDataPage.generalInformationCard.businessSize.placeholder',
              )}
              options={businessSizeOptions}
              value={values.businessSize}
              onChange={onChange}
            />
            {!showTaxExceptionsCard && (
              <>
                <Select
                  name="type"
                  label={t(
                    'businessDataPage.generalInformationCard.type.label',
                  )}
                  placeholder={t(
                    'businessDataPage.generalInformationCard.type.placeholder',
                  )}
                  options={typeOptions}
                  value={values.type}
                  onChange={onChange}
                />
                {renderTaxesInfo()}
              </>
            )}
          </Stack>
        </BaseCard.Body>
      </BaseCard>
      {showTaxExceptionsCard && <TaxExceptionsCard />}
    </>
  );
}

export default GeneralInformationBusinessDataCard;
